/**
 * What a bundle's card that can use a card you have asks of the person
 * (keepr-api KPR-1811, SCHEMA § Using a card you have; plan v4 §5.12): the
 * install dialog's *What it needs* pane, as data. Pure: the preview's
 * `requirements` in, the pane's choices, the `bindings` body, what is still to
 * choose and the lines it says, out. The server judges everything again; this
 * module only decides what to ask and what to send.
 *
 * A choice, per requirement (by its local id):
 *   { use: 'card' | 'own', cardId, elements, values, lists }
 *   elements  { <contract name>: { kind: 'match' | 'convert', id } | { kind: 'add', label } | null }
 *   values    { <contract name>: { <bundle value>: { kind: 'match', value } | { kind: 'add' } | null } }
 *   lists     { <list name>: 'add' | 'leave' | null }
 * `null` is unset. A row that needs a change to the person's card starts unset:
 * choosing it is the approval (D7).
 */

const own = (object, key) => Boolean(object) && Object.prototype.hasOwnProperty.call(object, key);
const list = value => (Array.isArray(value) ? value : []);
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** The preview's requirements, or none. */
export function requirementsOf(preview) {
	return list(preview?.requirements);
}

/** A requirement's candidate by card id, or null. */
export function candidateOf(requirement, cardId) {
	return list(requirement?.candidates).find(c => c.cardId === cardId) || null;
}

/**
 * Does the install need the pane? A requirement the server could not choose
 * for (no default that changes nothing, `requirement_unbound`), or an add-on
 * whose rows need the person's approval (`binding_needs_approval`). A preview
 * whose every requirement has a default is one step (D16).
 */
export function needsChoice(preview) {
	const code = preview?.refusal?.code;
	if (code === 'requirement_unbound' || code === 'binding_needs_approval') return requirementsOf(preview).length > 0;
	return requirementsOf(preview).some(r => !r.chosen && !r.provided);
}

/** The lists a candidate would put on the card (Q2): offered, never asked by default. */
export function listsOf(candidate) {
	return list(candidate?.adds).filter(a => a.kind === 'relatedList').map(a => a.name);
}

/** A row the pane asks about: not settled earlier, not waiting on an add-on not chosen, not deleted since. */
export const isAsked = row => !row.settled && !row.waits && !row.gone;

/**
 * The starting choice for one candidate: a match that fits as it is (tier 1)
 * is kept; a row read as it stands (tier 2) and anything that changes the card
 * start unset, since the person approves those (D7; KPR-1812's `not_approved`).
 */
export function startingCardChoice(candidate) {
	const elements = {};
	const values = {};
	list(candidate?.rows).filter(isAsked).forEach((row) => {
		elements[row.name] = row.target && row.tier === 1 && !row.convert && !row.add ? { kind: 'match', id: row.target } : null;
		if (Array.isArray(row.values)) {
			values[row.name] = {};
			row.values.forEach((v) => {
				values[row.name][v.value] = elements[row.name] && own(v, 'target') && !v.add ? { kind: 'match', value: v.target } : null;
			});
		}
	});
	const lists = {};
	listsOf(candidate).forEach((name) => { lists[name] = null; });
	return { use: 'card', cardId: candidate ? candidate.cardId : null, elements, values, lists };
}

/** The choice the pane opens on for a requirement: the server's, else its best card, else the bundle's own. */
export function startingChoice(requirement) {
	const candidates = list(requirement?.candidates);
	const chosen = requirement?.chosen;
	if (chosen && chosen.how === 'own') return { use: 'own', cardId: null, elements: {}, values: {}, lists: {} };
	const pick = (chosen && chosen.cardId && candidateOf(requirement, chosen.cardId)) || candidates[0] || null;
	if (!pick) return requirement?.own ? { use: 'own', cardId: null, elements: {}, values: {}, lists: {} } : { use: 'card', cardId: null, elements: {}, values: {}, lists: {} };
	return startingCardChoice(pick);
}

/** Is this requirement filled by a card another package brings (KPR-1829)? The server's default holds until the person picks a card of theirs. */
export const isProvided = r => Boolean(r?.provided) || r?.chosen?.how === 'dependency';

/** Is the bundle already bound here (an add-on added later, KPR-1812)? Its card is settled; only its new rows are asked. */
export const isSettled = r => list(r?.candidates).some(c => list(c.rows).some(row => row.settled));

/** The pane's starting choices for every requirement, by local id. A provided one has none: the dependency's card holds. */
export function startingChoices(preview) {
	const out = {};
	requirementsOf(preview).filter(r => !isProvided(r)).forEach((r) => { out[r.local] = startingChoice(r); });
	return out;
}

/** A row's values must be matched when the bundle's element is a choice and the row has a card element, matched or converted. */
const valuesAsked = (row, element) => Array.isArray(row.values) && Boolean(element) && element.kind !== 'add';

/**
 * The `bindings` body for the install, its preview, and the add-ons calls: one
 * entry per requirement the person chose for. What is unset is left out, so
 * the server says what is still to approve; a settled row is never sent.
 */
export function bindingsOf(requirements, choices) {
	const out = {};
	list(requirements).forEach((r) => {
		const choice = choices?.[r.local];
		if (!choice) return;
		if (choice.use === 'own') { out[r.local] = { use: 'own' }; return; }
		const candidate = candidateOf(r, choice.cardId);
		if (!candidate) return;
		const elements = {};
		const values = {};
		list(candidate.rows).filter(isAsked).forEach((row) => {
			const element = choice.elements?.[row.name];
			if (!element) return;
			if (element.kind === 'add') elements[row.name] = { add: { label: element.label || row.label } };
			else if (element.kind === 'convert') elements[row.name] = { convert: 'choice', id: element.id };
			else elements[row.name] = element.id;
			if (!valuesAsked(row, element)) return;
			const map = {};
			row.values.forEach((v) => {
				const value = choice.values?.[row.name]?.[v.value];
				if (!value) return;
				map[v.value] = value.kind === 'add' ? { add: { label: v.label || v.value } } : value.value;
			});
			if (Object.keys(map).length) values[row.name] = map;
		});
		const relatedLists = Object.keys(choice.lists || {}).filter(name => choice.lists[name] === 'add');
		out[r.local] = {
			use: 'card',
			cardId: candidate.cardId,
			// `null` is an old card with no timestamp (SCHEMA): sent as the preview gave it.
			ifUpdatedAt: own(candidate, 'updatedAt') ? candidate.updatedAt : null,
			...(Object.keys(elements).length ? { elements } : {}),
			...(Object.keys(values).length ? { values } : {}),
			...(relatedLists.length ? { relatedLists } : {}),
		};
	});
	return out;
}

/** What is still to choose: `{ elements, values, lists }`, counted over every requirement. */
export function stillToChoose(requirements, choices) {
	const left = { elements: 0, values: 0, lists: 0, cards: 0 };
	list(requirements).forEach((r) => {
		const choice = choices?.[r.local];
		if (!choice || choice.use !== 'card') return;
		const candidate = candidateOf(r, choice.cardId);
		if (!candidate) { left.cards += 1; return; }
		list(candidate.rows).filter(isAsked).forEach((row) => {
			const element = choice.elements?.[row.name];
			if (!element) { left.elements += 1; return; }
			if (element.kind === 'add' && !(element.label || '').trim()) { left.elements += 1; return; }
			if (valuesAsked(row, element)) row.values.forEach((v) => { if (!choice.values?.[row.name]?.[v.value]) left.values += 1; });
		});
		listsOf(candidate).forEach((name) => { if (!choice.lists?.[name]) left.lists += 1; });
	});
	return left;
}

/** The reason beside a disabled primary, or '' when everything is chosen ("2 elements still to match"). */
export function stillToChooseLine(left) {
	if (!left) return '';
	const parts = [];
	if (left.cards) parts.push(`${left.cards === 1 ? 'A card' : plural(left.cards, 'card')} still to choose`);
	const match = [];
	if (left.elements) match.push(plural(left.elements, 'element'));
	if (left.values) match.push(plural(left.values, 'value'));
	if (match.length) parts.push(`${match.join(' and ')} still to match`);
	if (left.lists) parts.push(`${plural(left.lists, 'list')} still to choose`);
	return parts.join('; ');
}

/** Tags beside a candidate in the card Select: what makes it a fit, said in a word or two. */
export function fitTags(requirement, candidate) {
	const tags = [];
	if (candidate.key && candidate.key === requirement.key) tags.push('Same kind');
	list(candidate.usedBy).forEach(u => tags.push(`Used by ${u.name || u.package}`));
	if (list(candidate.problems).length) tags.push('Does not fit');
	else if (candidate.changes) tags.push('Needs changes');
	else if (candidate.fits && candidate.tier === 1) tags.push('Fits as it is');
	return tags;
}

/**
 * What each need uses, for the one-step line ("Uses your Staff card for Staff
 * member."), from the preview's `chosen`. A new collection has no preview: its
 * needs are the bundle's own cards (`requirementsOfPackage`).
 */
export function usesLines(requirements) {
	return list(requirements).map((r) => {
		const name = r.card?.name || 'its card';
		const chosen = r.chosen;
		if (chosen && chosen.how === 'card') {
			const candidate = candidateOf(r, chosen.cardId);
			return candidate?.name ? `Uses your ${candidate.name} card for ${name}.` : `Uses a card you have for ${name}.`;
		}
		if (chosen && chosen.how === 'dependency') return `Uses the ${name} card from ${chosen.package}.`;
		if (r.own && r.own.note) return r.own.note;
		return `Adds its own ${name} card.`;
	});
}

/** The package page's and a new collection's view of what a bundle can use (GET /api/packages/{key} `requirements`). */
export function requirementsOfPackage(pkg) {
	return list(pkg?.requirements).filter(r => r && r.card);
}

/** "First name, Status and Next shift" for a contract line. */
export function contractWords(contract) {
	const names = list(contract).map(el => (typeof el.label === 'string' ? el.label : el.label?.singular) || el.name).filter(Boolean);
	if (names.length < 2) return names.join('');
	return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * The problems the preview found on one requirement's rows, by contract name,
 * leaving out what the count already says (a row or a value still unset).
 */
const COUNTED = new Set(['unmatched', 'value_unmatched', 'conversion_needed', 'not_approved']);
export function rowProblems(preview, local, { counted = false } = {}) {
	const out = {};
	// `counted`: the pane's own count is empty, so what the server still asks is said on its row.
	const problems = list(preview?.refusal?.problems).filter(p => (!p.local || p.local === local) && (counted || !COUNTED.has(p.why)));
	problems.forEach((p) => {
		const key = p.element || '';
		out[key] = [...(out[key] || []), p.reason || p.message].filter(Boolean);
	});
	return out;
}
